<script setup lang="ts">
import { gsap } from 'gsap'
import { MotionPathPlugin } from 'gsap/MotionPathPlugin'

/**
 * Data Connectors: Daten aus der EIGENEN Infrastruktur abfragen und dem
 * Support direkt anschaulich ins Ticket legen. Aufbau im exakten
 * Karten-Vokabular der Integrations-Section (sauberes Grid), plus:
 *  - das Flow-Band: links die Modal-Eingabe des Members (PeekModal), rechts
 *    die Bot-Nachricht im Ticket (PeekMessage), dazwischen weiße Saiten, auf
 *    denen Datenpakete RANDOM entlangfliegen (zufällige Bahn, Dauer,
 *    Abstände — GSAP MotionPath, pausiert außerhalb des Viewports)
 *  - die ruhige Demo-Karte: API-Antwort → ein Klick → Bot-Nachricht im Ticket.
 *
 * Entrances (useEntrance): das Flow-Band baut sich in Datenrichtung auf —
 * beide Seiten fahren ein, dann ziehen sich die Saiten Member → API → Team
 * (Clip-Sweep; DrawSVG verträgt sich nicht mit non-scaling-stroke) und der
 * API-Chip rastet ein; die Feature-Zeile steigt mit eindrehenden Glyphen.
 * Die Demo-Karte spielt ihren eigenen Ablauf: die Felder der Antwort werden
 * nacheinander „angeklickt", dann Klick-Hinweis, dann die Karte im Ticket.
 */

const sectionRef = ref<HTMLElement | null>(null)
useReveal(sectionRef)
useSpotlight(sectionRef)

/* Token-Farben für Farb-Tweens (GSAP interpoliert keine var()-Strings) */
const tokenColor = (name: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim()
const withZeroAlpha = (rgba: string) =>
  rgba.startsWith('rgba(') ? rgba.replace(/[\d.]+\s*\)$/, '0)') : 'transparent'

useEntrance(sectionRef, ({ q, tl }) => {
  tl.from(q('.cx-flow'), { opacity: 0, y: 24, duration: 0.7, clearProps: 'transform' }, 0)
  tl.from(q('.cx-flow-side'), {
    opacity: 0,
    x: (i: number) => (i === 0 ? -18 : 18),
    duration: 0.7,
    stagger: 0.12,
    clearProps: 'transform'
  }, 0.2)
  tl.fromTo(q('.cx-flow-svg'),
    { clipPath: 'inset(0% 100% 0% 0%)' },
    { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.5, ease: 'power2.inOut', clearProps: 'clipPath' }, 0.4)
  tl.from(q('.cx-flow-api'), { opacity: 0, scale: 0.6, duration: 0.55, ease: 'back.out(2)', clearProps: 'transform' }, 0.95)

  tl.from(q('.cx-feature'), { opacity: 0, y: 18, duration: 0.7, stagger: 0.1, clearProps: 'transform' }, 0.55)
  tl.from(q('.cx-feature-glyph'), {
    scale: 0.4,
    rotate: -20,
    duration: 0.6,
    ease: 'back.out(2)',
    stagger: 0.1,
    clearProps: 'transform'
  }, 0.7)
}, { trigger: '.connectors-grid' })

useEntrance(sectionRef, ({ q, tl }) => {
  tl.from(q('.cx-demo'), { opacity: 0, y: 24, duration: 0.7, clearProps: 'transform' }, 0)
  tl.from(q('.cx-mark'), {
    backgroundColor: withZeroAlpha(tokenColor('--nb-accent-tint')),
    color: tokenColor('--nb-ink-faint'),
    duration: 0.45,
    stagger: 0.24,
    clearProps: 'backgroundColor,color'
  }, 0.5)
  tl.from(q('.cx-demo-arrow'), { opacity: 0, x: -10, duration: 0.5, clearProps: 'transform' }, 1.2)
  tl.from(q('.cx-demo-card'), { opacity: 0, x: 18, duration: 0.6, clearProps: 'transform' }, 1.35)
  tl.from(q('.cx-demo-card .peek-row'), { opacity: 0, y: 8, duration: 0.45, stagger: 0.14, clearProps: 'transform' }, 1.55)
}, { trigger: '.cx-demo', start: 'top 85%' })

let ctx: gsap.Context | undefined
let observer: IntersectionObserver | undefined
let stopFlows: (() => void) | undefined

onMounted(async () => {
  await nextTick()
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  gsap.registerPlugin(MotionPathPlugin)

  ctx = gsap.context((self) => {
    const q = self.selector!
    const reqPaths = gsap.utils.toArray<SVGPathElement>(q('.cx-string-req'))
    const resPaths = gsap.utils.toArray<SVGPathElement>(q('.cx-string-res'))
    const allPaths = [...reqPaths, ...resPaths]
    const dots = gsap.utils.toArray<HTMLElement>(q('.cx-dot'))
    const orderPacket = q('.cx-packet-order')[0] as HTMLElement | undefined
    const apiGlow = q('.cx-flow-api-glow')[0] as HTMLElement | undefined
    const cardGlow = q('.cx-flow-card-glow')[0] as HTMLElement | undefined
    const band = q('.cx-flow')[0] as HTMLElement | undefined
    if (!allPaths.length || !dots.length || !band) return

    gsap.set([...dots, orderPacket, apiGlow, cardGlow].filter(Boolean), { autoAlpha: 0 })

    let running = false

    const rand = (min: number, max: number) => min + Math.random() * (max - min)
    const pick = <T>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)]!

    const glowPulse = (el: HTMLElement | undefined) => {
      if (!el) return
      gsap.timeline()
        .to(el, { autoAlpha: 1, duration: 0.18 })
        .to(el, { autoAlpha: 0, duration: 0.55 }, 0.28)
    }

    /* Ein Flug entlang einer Saite (Dot ODER das beschriftete Order-Paket) */
    const travel = (el: HTMLElement, path: SVGPathElement, dur: number, onDone?: () => void) => {
      const tl = gsap.timeline({ onComplete: onDone })
      tl.to(el, {
        motionPath: { path, align: path, alignOrigin: [0.5, 0.5] },
        duration: dur,
        ease: 'none'
      }, 0)
      tl.to(el, { autoAlpha: 1, duration: 0.22 }, 0)
      tl.to(el, { autoAlpha: 0, duration: 0.28 }, dur - 0.28)
    }

    /* Die Story im Loop: Order-Nr. → deine API → Antwort → Karte leuchtet */
    const storyCycle = () => {
      if (!running || !orderPacket) return
      travel(orderPacket, pick(reqPaths), rand(1.3, 1.9), () => {
        glowPulse(apiGlow)
        gsap.delayedCall(0.22, () => {
          if (!running) return
          const responder = dots[0]!
          travel(responder, pick(resPaths), rand(0.9, 1.3), () => {
            glowPulse(cardGlow)
            gsap.delayedCall(rand(0.8, 2.2), storyCycle)
          })
        })
      })
    }

    /* Ambient: restliche Pakete fliegen random über alle Saiten */
    const ambient = (dot: HTMLElement) => {
      if (!running) return
      travel(dot, pick(allPaths), rand(1.5, 2.9), () => {
        gsap.delayedCall(rand(0.3, 1.4), () => ambient(dot))
      })
    }

    // Erster Start wartet, bis die Entrance die Saiten gezogen hat
    let primed = false
    const start = () => {
      if (running) return
      running = true
      gsap.delayedCall(primed ? 0.4 : 2.2, storyCycle)
      primed = true
      dots.slice(1).forEach((dot, i) => gsap.delayedCall(rand(0.2, 1.6) + i * 0.3, () => ambient(dot)))
    }
    const stop = () => {
      running = false
    }
    // Auch beim Unmount erreichbar — die delayedCall-Ketten laufen außerhalb
    // des Kontexts weiter, ctx.revert() allein stoppt sie nicht.
    stopFlows = stop

    /* Sichtbarkeit robust über IntersectionObserver (spart Arbeit off-screen) */
    observer = new IntersectionObserver(
      entries => entries.forEach(e => (e.isIntersecting ? start() : stop())),
      { threshold: 0.15 }
    )
    observer.observe(band)
  }, sectionRef.value!)
})

onBeforeUnmount(() => {
  stopFlows?.()
  observer?.disconnect()
  ctx?.revert()
})

const features = [
  {
    icon: 'i-lucide-plug',
    title: 'Plug in any system',
    text: 'Paste the endpoint of your shop, CRM or license server and add its credentials. Any JSON API works.'
  },
  {
    icon: 'i-lucide-mouse-pointer-click',
    title: 'Map fields with a click',
    text: 'Run a test fetch, click the fields you want on the ticket, and Clarify builds the card blueprint for you.'
  },
  {
    icon: 'i-lucide-scan-face',
    title: 'Queried when a ticket opens',
    text: 'Attach a connector to a category and it runs the moment a ticket opens, using the member’s Discord ID or their answers.'
  },
  {
    icon: 'i-lucide-eye-off',
    title: 'Credentials stay hidden',
    text: 'Once saved, credentials are never shown again in the dashboard. When you edit a connector, the field simply reads “leave blank to keep”.'
  }
]
</script>

<template>
  <section id="connectors" ref="sectionRef" class="connectors">
    <span class="section-rule" aria-hidden="true" />
    <div class="section-shell">
      <div class="section-head">
        <h2 class="h-section" data-split>Your own data sources. No code.</h2>
        <p class="copy-muted reveal connectors-intro">
          Connect your own shop, CRM or license server and its data appears on the
          ticket, right where your team works. You set it up by clicking, not coding.
        </p>
      </div>

      <div class="connectors-grid">
        <!-- Flow-Band: die Runtime-Story — Modal-Eingabe → deine API → Support -->
        <article class="card-shell cx-flow entrance" aria-hidden="true">
          <span class="cell-plus cell-plus-tl" />
          <span class="cell-plus cell-plus-br" />
          <div class="cx-flow-side">
            <span class="cx-flow-caption">the member</span>
            <!-- Die Order-Nummer ist eine Modal-Eingabe des Members -->
            <PeekModal class="cx-flow-modal" title="Billing" label="Order number" value="4821-STRIPE" />
          </div>

          <div class="cx-flow-stage">
            <svg class="cx-flow-svg" viewBox="0 0 600 160" preserveAspectRatio="none" fill="none">
              <!-- Requests: Modal → deine API -->
              <path class="cx-string cx-string-req" d="M 2 30 C 140 30, 190 80, 292 80" />
              <path class="cx-string cx-string-req" d="M 2 80 L 292 80" />
              <path class="cx-string cx-string-req" d="M 2 130 C 140 130, 190 80, 292 80" />
              <!-- Responses: deine API → Ticket-Karte -->
              <path class="cx-string cx-string-res" d="M 308 80 C 420 60, 480 80, 598 80" />
              <path class="cx-string cx-string-res" d="M 308 80 C 420 100, 480 80, 598 80" />
            </svg>

            <!-- Deine API in der Mitte der Bahn -->
            <span class="cx-flow-chip cx-flow-api">
              <span class="cx-flow-api-glow" />
              <UIcon name="i-lucide-braces" class="size-[13px]" />
              your API
            </span>

            <!-- Das Order-Paket (Request) + Response-/Ambient-Pakete -->
            <span class="cx-packet-order">4821-STRIPE</span>
            <span v-for="n in 4" :key="n" class="cx-dot" />
          </div>

          <div class="cx-flow-side cx-flow-side-end">
            <span class="cx-flow-caption">your team</span>
            <!-- … und landet als Bot-Karte im Ticket-Thread -->
            <div class="cx-flow-card">
              <span class="cx-flow-card-glow" />
              <PeekMessage
                thread="jonas.-Billing"
                :ghost="false"
                title="Shop order"
                :rows="[
                  { key: 'Order', value: '4821-STRIPE', mono: true },
                  { key: 'Amount', value: '€9.99' },
                  { key: 'Status', value: 'paid', badge: true }
                ]"
              />
            </div>
          </div>
        </article>

        <!-- Vier Features: bewusst OHNE Karten-Chrome — Hairline statt Box -->
        <div class="cx-features shell-gutter">
          <div v-for="feature in features" :key="feature.title" class="cx-feature rule-dash entrance">
            <UIcon :name="feature.icon" class="cx-feature-glyph" aria-hidden="true" />
            <h3 class="cx-title">{{ feature.title }}</h3>
            <p class="cx-text copy-muted">{{ feature.text }}</p>
          </div>
        </div>

        <!-- Breite Demo-Karte: Antwort → ein Klick → Bot-Nachricht im Ticket -->
        <article class="card-shell cx-card cx-demo spot-card entrance">
          <span class="cx-icon" aria-hidden="true">
            <UIcon name="i-lucide-wand-sparkles" class="cx-icon-glyph" />
          </span>
          <h3 class="cx-title">From response to ticket card</h3>
          <p class="cx-text copy-muted">
            Click the fields you need in your system’s response and Clarify posts them as a card on every ticket.
          </p>

          <!-- Nur die Illustration ist dekorativ — Titel + Copy bleiben lesbar -->
          <div class="cx-demo-flow" aria-hidden="true">
            <pre class="cx-demo-json"><code>{
  "customer": <span class="cx-mark">"Jonas M."</span>,
  "status":   <span class="cx-mark">"Premium"</span>,
  "license":  <span class="cx-mark">"CL-8241-PRO"</span>
}</code></pre>

            <span class="cx-demo-arrow">
              <UIcon name="i-lucide-mouse-pointer-click" class="size-[15px]" />
              one click
              <UIcon name="i-lucide-arrow-right" class="size-[15px] cx-demo-arrow-icon" />
            </span>

            <PeekMessage
              class="cx-demo-card"
              thread="jonas.-Billing"
              title="License server"
              :rows="[
                { key: 'Customer', value: 'Jonas M.' },
                { key: 'Status', value: 'Premium', badge: true },
                { key: 'License', value: 'CL-8241-PRO', mono: true }
              ]"
            />
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<style scoped>
.connectors {
  position: relative;
  padding: var(--section-gap) 0 0;
}

.connectors-intro {
  max-width: 56ch;
}

/* Flow-Band oben, Feature-Zeile ohne Boxen, Demo-Karte volle Breite */
.connectors-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  margin-top: 72px;
}

.cx-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  padding: 26px 24px;
}

.cx-card:hover {
  border-color: var(--nb-hairline-strong);
}

/* Features: Hairline-Gruppe statt Karten-Grid — andere Familie als das
   Integrations-Bento, gleiche Sprache */
.cx-features {
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px 32px;
  padding-top: 10px;
}

.cx-feature {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding-top: 30px;
  border-top: 1px solid var(--nb-hairline);
}

.cx-feature-glyph {
  width: 20px;
  height: 20px;
  color: var(--nb-accent-soft);
  margin-bottom: 2px;
}

.cx-icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.01));
  border: 1px solid var(--nb-hairline);
}

.cx-icon-glyph {
  width: 22px;
  height: 22px;
  color: var(--nb-accent-soft);
}

.cx-title {
  font-size: 1.08rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--nb-ink);
}

.cx-text {
  font-size: 0.94rem;
  line-height: 1.55;
}

/* ── Flow-Band: weiße Saiten, random fliegende Datenpakete ──────────────── */
.cx-flow {
  position: relative; /* Anker für die Corner-Plus-Marks */
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 26px;
  padding: 24px 28px;
}

.cx-flow-side {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
}

.cx-flow-side-end {
  align-items: flex-end;
}

.cx-flow-caption {
  font-family: var(--font-mono);
  font-size: 0.64rem;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--nb-ink-faint);
  margin-bottom: 2px;
}

.cx-flow-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 11px;
  background: var(--nb-elevated);
  border: 1px solid var(--nb-hairline);
  border-radius: var(--radius-pill);
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--nb-ink-muted);
  white-space: nowrap;
}

/* Die Modal-Eingabe des Members (PeekModal) */
.cx-flow-modal {
  width: 232px;
}

/* Deine API — sitzt mitten auf der Bahn */
.cx-flow-api {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  background: var(--nb-surface);
}

.cx-flow-api-glow {
  position: absolute;
  inset: -5px;
  border-radius: var(--radius-pill);
  border: 1px solid rgba(255, 255, 255, 0.55);
  box-shadow: 0 0 16px rgba(255, 255, 255, 0.25);
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
}

/* Das beschriftete Order-Paket (Request) */
.cx-packet-order {
  position: absolute;
  top: 0;
  left: 0;
  font-family: var(--font-mono);
  font-size: 0.62rem;
  font-weight: 600;
  color: var(--nb-bg);
  background: #fff;
  border-radius: var(--radius-pill);
  padding: 2px 7px;
  box-shadow: 0 0 12px 2px rgba(255, 255, 255, 0.35);
  will-change: transform;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
}

/* Die Bot-Nachricht im Ticket (PeekMessage) — Wrapper trägt den Glow */
.cx-flow-card {
  position: relative;
  width: 268px;
}

.cx-flow-card-glow {
  position: absolute;
  inset: -1px;
  border-radius: var(--radius-md);
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 0 18px rgba(255, 255, 255, 0.22);
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
}

.cx-flow-stage {
  position: relative;
  flex: 1;
  height: 132px;
  min-width: 0;
}

/* Graph-Paper unter der Flugbahn — die Bühne bekommt technische Struktur */
.cx-flow-stage::before {
  content: '';
  position: absolute;
  inset: -12px 0;
  background-image:
    linear-gradient(var(--nb-rail) 1px, transparent 1px),
    linear-gradient(90deg, var(--nb-rail) 1px, transparent 1px);
  background-size: 26px 26px;
  mask-image: radial-gradient(70% 100% at 50% 50%, #000 30%, transparent 78%);
  -webkit-mask-image: radial-gradient(70% 100% at 50% 50%, #000 30%, transparent 78%);
  pointer-events: none;
}

.cx-flow-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

/* Die Saiten: weiß, hauchdünn — wie die Hairlines des Themes */
.cx-string {
  stroke: rgba(255, 255, 255, 0.22);
  stroke-width: 1.25;
  vector-effect: non-scaling-stroke;
}

/* Datenpakete: weiß, dezenter Schein — default unsichtbar (GSAP blendet ein;
   bei reduced-motion bleiben sie damit sauber verschwunden) */
.cx-dot {
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 10px 2px rgba(255, 255, 255, 0.35);
  will-change: transform;
  opacity: 0;
  visibility: hidden;
}

@media (max-width: 639px) {
  .cx-flow {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .cx-flow-side,
  .cx-flow-side-end {
    align-items: stretch;
  }

  .cx-flow-modal,
  .cx-flow-card {
    width: 100%;
  }

  .cx-flow-stage {
    height: 120px; /* Luft für die Pakete zwischen Modal und Ticket-Karte */
  }
}

/* ── Demo-Karte ─────────────────────────────────────────────────────────── */
.cx-demo-flow {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  align-items: center;
  width: 100%;
  margin-top: 8px;
}

.cx-demo-json {
  margin: 0;
  padding: 14px 16px;
  background: var(--nb-bg);
  border: 1px solid var(--nb-hairline);
  border-radius: var(--radius-sm);
  font-family: var(--font-mono);
  font-size: 0.78rem;
  line-height: 1.7;
  color: var(--nb-ink-faint);
  overflow-x: auto;
}

.cx-mark {
  color: var(--nb-accent-soft);
  background: var(--nb-accent-tint);
  border-radius: 4px;
  padding: 1px 4px;
  cursor: default;
}

.cx-demo-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-family: var(--font-mono);
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nb-ink-faint);
  white-space: nowrap;
}

.cx-demo-arrow-icon {
  color: var(--nb-accent-soft);
}

.cx-demo-card {
  width: 100%;
}

@media (min-width: 640px) {
  .connectors-grid {
    gap: 24px;
  }

  .cx-features {
    grid-template-columns: repeat(2, 1fr);
  }

  .cx-demo-flow {
    grid-template-columns: 1.1fr auto 0.9fr;
    gap: 22px;
  }
}

@media (min-width: 1000px) {
  .cx-features {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
